import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { auth, setAuthToken } from '../../api';

const Input = ({ label, className = "", ...props }) => (
  <div className="flex flex-col gap-1.5 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <input 
      className={`
        flex h-12 w-full rounded-2xl border border-transparent bg-slate-100 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400
        focus:outline-none focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-200
        disabled:cursor-not-allowed disabled:opacity-50
        ${className}
      `}
      {...props}
    />
  </div>
);

const LoginScreen = ({ onLogin }) => {
    const [view, setView] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [resetStep, setResetStep] = useState(1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (view === 'login') {
                const res = await auth.login(email.trim(), password);
                setAuthToken(res.access_token);
                if (rememberMe) {
                    localStorage.setItem('rememberedEmail', email);
                } else {
                    localStorage.removeItem('rememberedEmail');
                }
                const me = await auth.me();
                onLogin(me);
            } else if (view === 'forgot') {
                if (resetStep === 1) {
                    await auth.requestPasswordReset(email.trim());
                    alert('Kod pengesahan telah dihantar ke e-mel anda. Sila semak peti masuk anda.');
                    setResetStep(2);
                } else {
                    await auth.verifyPasswordReset(email.trim(), verificationCode.trim(), newPassword);
                    alert('Kata laluan berjaya dikemas kini! Sila log masuk dengan kata laluan baharu.');
                    setView('login');
                    setResetStep(1);
                    setPassword('');
                    setNewPassword('');
                    setVerificationCode('');
                }
            }
        } catch (err) {
            console.error("Login Error:", err);
            let msg = "Ralat rangkaian atau pelayan.";
            if (err.response) {
                msg = err.response.data?.detail || `Ralat Pelayan (${err.response.status})`;
            } else if (err.request) {
                msg = "Tiada respons dari pelayan. Sila semak sambungan internet.";
            } else {
                msg = err.message;
            }
            alert(`${view === 'login' ? 'Log Masuk' : 'Reset Kata Laluan'} Gagal: ${msg}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const remembered = localStorage.getItem('rememberedEmail');
        if (remembered) {
            setEmail(remembered);
            setRememberMe(true);
        }
    }, []);

    return (
        <div className="min-h-[100dvh] w-full flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 font-sans safe-area-inset">
            <div className="flex-1 flex items-center justify-center px-4 py-8 sm:p-6">
                <div className="w-full max-w-md bg-white rounded-3xl sm:rounded-[2.5rem] shadow-xl p-6 sm:p-10 border border-slate-200/50">
                    <div className="text-center mb-8 sm:mb-10">
                        <div className="w-20 h-20 sm:w-32 sm:h-32 mx-auto mb-1 flex items-center justify-center">
                            <img src="/logo-main.png" alt="MS StockFlow Logo" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-1">MS StockFlow</h1>
                        <p className="text-slate-500 font-medium text-sm sm:text-base">
                            {view === 'forgot' ? 'Reset Kata Laluan Anda' : 'Sistem Pengurusan Inventori Pintar'}
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                        <Input label="Alamat Emel" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="nama@syarikat.com" />
                        {view === 'login' && (
                            <>
                                <Input label="Kata Laluan" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" />
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                        />
                                        <span className="text-sm text-slate-600">Ingat saya</span>
                                    </label>
                                    <button 
                                        type="button"
                                        onClick={() => { setView('forgot'); setPassword(''); setNewPassword(''); }} 
                                        className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
                                    >
                                        Lupa kata laluan?
                                    </button>
                                </div>
                            </>
                        )}
                        {view === 'forgot' && resetStep === 2 && (
                            <>
                                <Input 
                                    label="Kod Pengesahan" type="text" value={verificationCode} 
                                    onChange={e => setVerificationCode(e.target.value.toUpperCase())} 
                                    required placeholder="Masukkan kod 6 digit" maxLength="6"
                                />
                                <Input 
                                    label="Kata Laluan Baharu" type="password" value={newPassword} 
                                    onChange={e => setNewPassword(e.target.value)} 
                                    required placeholder="••••••••" 
                                />
                            </>
                        )}
                        
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="flex items-center justify-center w-full mt-4 sm:mt-6 py-3.5 sm:py-4 text-base font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
                            data-testid="login-submit-btn"
                        >
                            {isLoading ? <Loader2 size={22} className="animate-spin" /> : (
                                view === 'login' ? "Log Masuk" : 
                                resetStep === 1 ? "Hantar Kod Pengesahan" : "Reset Kata Laluan"
                            )}
                        </button>
                    </form>
                    {view === 'forgot' && (
                        <div className="mt-6 text-center">
                            <button onClick={() => { setView('login'); setResetStep(1); setPassword(''); setNewPassword(''); setVerificationCode(''); }} className="text-sm font-semibold text-slate-600 hover:text-emerald-600 transition-colors">
                                Kembali ke log masuk
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <footer className="text-center py-4 sm:py-6 text-xs sm:text-sm text-slate-400">
                © mayamedia.my {new Date().getFullYear()}
            </footer>
        </div>
    );
};

export default React.memo(LoginScreen);
